import { useState, useEffect, useRef, useMemo } from "react";
import { useModal } from "../context2/ModalContext";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
  useSpring,
} from "framer-motion";
import {
  Menu,
  X,
  ChevronDown,
  Star,
  ChevronLeft,
  ChevronRight,
  Home,
  Plus,
  Building2,
  History,
  LogOut,
  LogIn,
} from "lucide-react";
import Icon from "../components/Icon";
import { Link, useNavigate, useLocation  } from "react-router-dom";
import LogoWhiteText from "../assets/logo-white-text.svg";
import API from "@/services/api";
import TwoColumnSection2 from "@/components/TwoColumnSection2";
import FeaturesPage from "@/components/FeaturesPage";
import CommunitySignup from "@/components/CommunitySignup";
import TwoColumnSection from "@/components/TwoColumnSection";
import IntroVideo from "../assets/Omnishare Ad.00.mp4";
import { notify } from "@/utils/toast";
import z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import OmniVideo from "../assets/video/omnishare.mp4";
import LanguageDropdown from "../components/LanguageDropdown";
import { Trans, useTranslation } from "react-i18next";
import { useAppContext } from "../context/AppContext";
import Cookies from "js-cookie";
import { useUser } from "@/store/useUser";
import user from "pusher-js/types/src/core/user";
import VideoModal from "../components/modals/VideoModal";
import LogoutModal from '../components/modals/LogoutModal';
function HomePage() {
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { logout }: any = useAppContext();
  const { user }: any = useUser();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(
    null,
  );
  const [showContactForm, setShowContactForm] = useState(false);
  const isInitialMount = useRef(true);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
  });
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  useEffect(() => {
    document.title =
      "OmniShare - AI-Powered Social Media Content Generator & Scheduler";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "OmniShare is an AI-powered social media management platform that helps you create, schedule, and publish engaging content across Facebook, Instagram, YouTube, LinkedIn, and TikTok in seconds.",
      );
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://omnishare.ai",
        },
      ],
    };

    let schemaScript: any = document.querySelector(
      'script[data-type="breadcrumb-schema"]',
    );
    if (!schemaScript) {
      schemaScript = document.createElement("script");
      schemaScript.type = "application/ld+json";
      schemaScript.setAttribute("data-type", "breadcrumb-schema");
      schemaScript.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(schemaScript);
    }

    return () => {};
  }, []);

  const reviews = [
    {
      name: "Sophie Jameson",
      role: "Social Media Manager",
      company: "TechFlow Inc",
      rating: 5,
      text: t("testimonial_1"),
      avatar: "SJ",
    },
    {
      name: "Kevin Liu",
      role: "Marketing Director",
      company: "GrowthLabs",
      rating: 5,
      text: t("testimonial_2"),
      avatar: "KL",
    },
    {
      name: "Natalie Cruz",
      role: "Content Creator",
      company: "Creative Studio",
      rating: 5,
      text: t("testimonial_3"),
      avatar: "NC",
    },
    {
      name: "Matthew Sullivan",
      role: "CEO",
      company: "StartupHub",
      rating: 5,
      text: t("testimonial_4"),
      avatar: "MS",
    },
    {
      name: "Camila Rivera",
      role: "Brand Manager",
      company: "Fashion Forward",
      rating: 5,
      text: t("testimonial_5"),
      avatar: "CR",
    },
  ];

  const [expandedFaqIndex, setExpandedFaqIndex] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(6);

  const faqs = [
    {
      question: t("what_is_omnishare"),
      answer: t("create_publish_message"),
    },
    {
      question: t("ai_content_creation"),
      answer: t("ai_content_creation_message"),
    },

    {
      question: t("platform_support"),
      answer: t("platform_support_message"),
    },
    {
      question: t("multi_publish"),
      answer: t("multi_publish_message"),
    },
    {
      question: t("faq_security_audits"),
      answer: t("faq_security_audits_answer"),
    },
    {
      question: t("does_omnishare_optimize_media_for_each_platform"),
      answer: t("omnishare_auto_resizes_description"),
    },
    {
      question: t("insights"),
      answer: t("insights_message"),
    },
    {
      question: t("publishing_speed"),
      answer: t("publishing_speed_message"),
    },
    {
      question: t("trend_content"),
      answer: t("trend_content_message"),
    },
    {
      question: t("business_suitability"),
      answer: t("business_suitability_message"),
    },
    {
      question: t("demos"),
      answer: t("demos_message"),
    },
    {
      question: t("faq_how_is_my_data_protected"),
      answer: t("faq_how_is_my_data_protected_answer"),
    },
    {
      question: t("faq_does_omnishare_store_credentials"),
      answer: t("faq_does_omnishare_store_credentials_answer"),
    },
    {
      question: t("faq_can_multiple_team_members_access"),
      answer: t("faq_can_multiple_team_members_access_answer"),
    },
    {
      question: t("faq_prevent_unauthorized_access"),
      answer: t("faq_prevent_unauthorized_access_answer"),
    },
    {
      question: t("faq_privacy_compliance"),
      answer: t("faq_privacy_compliance_answer"),
    },
    {
      question: t("faq_security_issue"),
      answer: t("faq_security_issue_answer"),
    },
  ];

  const displayedFaqs = faqs.slice(0, visibleCount);

  const nextReview = () => {
    setSlideDirection("left");
    setTimeout(() => {
      setCurrentReviewIndex((prev) => (prev + 1) % reviews.length);
      setSlideDirection(null);
    }, 300);
  };

  const prevReview = () => {
    setSlideDirection("right");
    setTimeout(() => {
      setCurrentReviewIndex(
        (prev) => (prev - 1 + reviews.length) % reviews.length,
      );
      setSlideDirection(null);
    }, 300);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
  };

  const contactSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(2, t("first_name_required")),
        lastName: z.string().min(2, t("last_name_required")),
        email: z
          .string()
          .min(1, t("please_enter_email_address"))
          .email(t("please_enter_valid_email")),
        message: z.string().min(5, t("please_enter_message")),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(contactSchema),
  });
  const onSubmit = async (data: any) => {
    try {
      await API.contactUs(data);

      notify("success", "t(message_sent_successfully)");
      reset();
    } catch (err: any) {
      notify("error", err.response?.data?.message || t("something_went_wrong"));
    }
  };

  useEffect(() => {
  const hash = location.hash || "";
  if (hash !== "#Contact") return; // exact, case-sensitive match

  const scrollToContact = () => {
    const el =
      document.getElementById("Contact") ||
      document.getElementById("contact") ||
      document.querySelector('[data-section="contact"]');

    if (!el) return false;

    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: "smooth" });
    return true;
  };

  let tries = 0;
  const maxTries = 30;

  const tick = () => {
    tries += 1;
    const ok = scrollToContact();
    if (ok) return;
    if (tries < maxTries) requestAnimationFrame(tick);
    else {
      // optional fallback if you still want one when hash is exactly "#Contact"
      window.scrollTo({ top: document.body.scrollHeight * 0.85, behavior: "smooth" });
    }
  };

  requestAnimationFrame(tick);
}, [location.hash]);


  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setIsMenuOpen(false)}
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-y-0 left-0 z-50 w-full sm:w-[60%] bg-[#7650e3] transform lg:hidden overflow-y-auto"
          >
            <div className="flex items-center justify-between border-b border-white/20 p-2 py-3">
              <span className="flex items-center">
                <Icon
                  name="spiral-logo"
                  className="ml-2 brightness-[200]"
                  size={38}
                />
                <span className="text-white text-2xl lg:text-[1.6rem] tracking-tight ml-3">
                  <img
                    src={LogoWhiteText}
                    alt="Omnishare logo"
                    className="h-4"
                    decoding="async"
                    loading="lazy"
                  />
                </span>
              </span>

              <button
                onClick={() => setIsMenuOpen(false)}
                className="rounded-md text-white mr-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <AnimatePresence>
              {Cookies.get("auth_token") && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="border-b border-white/20 p-2 overflow-hidden"
                >
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate("/dashboard");
                    }}
                    className="flex items-center gap-x-3 w-full hover:bg-white/10 rounded-md p-2 transition-colors"
                  >
                    <img
                      className="w-11 h-11 rounded-full object-cover border border-white/50"
                      src={
                        user?.avatarUrl
                          ? user?.avatarUrl
                          : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              user?.profile?.fullName || user?.email,
                            )}&background=00000000&color=fff`
                      }
                      alt={user?.profile?.fullName}
                    />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-md font-medium text-white truncate">
                        {user?.profile?.fullName || user?.email || "User"}
                      </div>
                      <div className="text-sm text-white/80 truncate">
                        {user?.email}
                      </div>
                    </div>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <nav className="flex-1 px-1 py-2.5 flex flex-col mx-2 gap-y-2">
              {[
                { id: "Home", name: t("home"), icon: Home },
                { id: "Features", name: t("features"), icon: Plus },
                { id: "Faq", name: t("faq"), icon: Building2 },
                { id: "Contact", name: t("contact"), icon: History },
              ].map((item, index) => (
                <motion.button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="flex items-center px-4 py-3 text-white font-medium rounded-md transition-colors hover:bg-[#d7d7fc] hover:text-[#7650e3]"
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <item.icon size={20} className="mr-3" />
                  <span>{item.name}</span>
                </motion.button>
              ))}
            </nav>

            <div className="px-1 mx-2 pb-2 space-y-4 ">
              <AnimatePresence mode="wait">
                {!Cookies.get("auth_token") && (
                  <motion.div
                    key="login-mobile"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col gap-3 w-full"
                  >
                    <motion.button
                      onClick={() => {
                        setIsMenuOpen(false);
                        if (Cookies.get("auth_token")) {
                          navigate("/content");
                        } else {
                          navigate("/auth");
                        }
                      }}
                      className="w-full bg-white text-[#7650e3] px-6 py-3 rounded-full font-semibold shadow-lg transition-all"
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t("get_started_free")}
                    </motion.button>
                    <motion.button
                      onClick={() => {
                        setIsMenuOpen(false);
                        if (Cookies.get("auth_token")) {
                          navigate("/content");
                        } else {
                          navigate("/auth");
                        }
                      }}
                      className="w-full flex justify-item  justify-center gap-2 bg-[#7650e3] text-white  hover:bg-[#d7d7fc] border border-white  px-6 py-3 rounded-full font-semibold shadow-lg transition-all items-center"
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <LogIn size={18} />
                      {t("login")}
                    </motion.button>
                  </motion.div>
                )}

                {Cookies.get("auth_token") && (
                  <motion.div
                    key="login-mobile"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col gap-3 w-full "
                  >
                    <motion.button
                      onClick={() => {
                        setIsMenuOpen(false);
                        if (Cookies.get("auth_token")) {
                          navigate("/content");
                        } else {
                          navigate("/auth");
                        }
                      }}
                      className="w-full bg-white text-[#7650e3] px-6 py-3 rounded-full font-semibold shadow-lg transition-all"
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>
                        {Cookies.get("auth_token")
                          ? t("create_post")
                          : t("get_started_free")}
                      </span>
                    </motion.button>
                    <motion.button
                      key="logout-mobile"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsLogoutModalOpen(true);
                      }}
                      className="w-full flex justify-item justify-center gap-4 bg-[#7650e3] text-white  hover:bg-[#d7d7fc] border border-white  px-6 py-3 rounded-full font-semibold shadow-lg transition-all items-center"
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <LogOut size={18} />
                      {t("logout")}
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className=" px-1 pb-2">
                <LanguageDropdown
                  alignRight={false}
                  showGlobe={true}
                  className="text-white"
                />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0">
              <div className="w-full mx-auto">
                <div className="text-center flex flex-col items-center justify-center border-y gap-3 border-white/20 relative p-2">
                  <div className="flex justify-center space-x-1 text-sm text-white/80 mb-1">
                    <Link
                      to="/privacy"
                      className="transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t("privacy_policy")}
                    </Link>
                    <span className="text-white/20">•</span>
                    <Link
                      to="/terms"
                      className="transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t("terms_service")}
                    </Link>
                    <span className="text-white/20">•</span>
                    <Link
                      to="/support"
                      className="transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t("support")}
                    </Link>
                  </div>
                  <div className="flex justify-center space-x-1 text-xs text-white/80 w-full">
                    {/* Social Media Icons */}
                    <div className="flex flex-row justify-between gap-1.5 px-5 w-full">
                     <a
  target="_blank"
  rel="noreferrer"
  className="hover:opacity-80 transition-opacity"
  href="https://www.facebook.com/share/1H74CpLTCK/"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M12 0C5.373 0 0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073c0-6.627-5.373-12-12-12z" fill="white"/>
  </svg>
</a>

{/* Instagram - White Glyph */}
<a
  target="_blank"
  rel="noreferrer"
  className="hover:opacity-80 transition-opacity"
  href="https://www.instagram.com/omnishare.ai/"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="white"/>
  </svg>
</a>

{/* LinkedIn - White Rounded Square with Cut-out 'in' */}
<a
  target="_blank"
  rel="noreferrer"
  className="hover:opacity-80 transition-opacity"
  href="https://www.linkedin.com/company/omnishare-ai"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M0 4a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4zm7.12 16.452H3.558V8.995H7.12v11.457zM5.34 7.433a2.064 2.064 0 1 1 0-4.128 2.064 2.064 0 0 1 0 4.128zM20.452 20.452h-3.558v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667h-3.558V8.995h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.291z" fill="white"/>
  </svg>
</a>

{/* TikTok - White Glyph */}
<a
  target="_blank"
  rel="noreferrer"
  className="hover:opacity-80 transition-opacity"
  href="https://www.tiktok.com/@omnishare.ai"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="24" height="24" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path d="M120 0h40c0 22 18 40 40 40v40c-14 0-27-4-40-12v88c0 44-36 80-80 80S0 200 0 156c0-44 36-80 80-80h40v40H80c-22 0-40 18-40 40s18 40 40 40 40-18 40-40V0z" fill="white"/>
  </svg>
</a>

{/* YouTube - White Play Button with Cut-out Triangle */}
<a
  target="_blank"
  rel="noreferrer"
  href="https://www.youtube.com/@OmniShare-ai"
  className="hover:opacity-80 transition-opacity"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="29" height="29" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="white"/>
  </svg>
</a>
                      
                    </div>
                  </div>
                </div>
                <div className="text-white/80 text-center text-sm mt-1 py-1">
                   {`© ${new Date().getFullYear()} OmniShare`}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className={`fixed top-0 w-full z-40 transition-all duration-500 ${
          scrollY > 50
            ? "bg-[#7650e3] shadow-lg backdrop-blur-md"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-[10%]">
          <div className="relative flex justify-between lg:justify-between items-center h-20 flex-row-reverse lg:flex-row">
            <div className="lg:hidden w-6 h-6"></div>

            <Link to="/">
              <motion.div
                className=" flex items-center space-x-2 lg:static lg:translate-x-0"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <Icon
                  name="spiral-logo"
                  size={35}
                  className=" brightness-[1000%]"
                />

                <img
                  src={LogoWhiteText}
                  alt="Omnishare logo"
                  className="h-5"
                  decoding="async"
                  loading="lazy"
                />
              </motion.div>
            </Link>

            <motion.button
              className={`lg:hidden transition-colors ${
                scrollY > 50 ? "text-white" : "text-white"
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileTap={{ scale: 0.9, rotate: 90 }}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </motion.button>

            <div className="hidden lg:flex items-center space-x-8">
              {[
                { id: "Home", label: t("home") },
                { id: "Features", label: t("features") },
                { id: "Faq", label: t("faq") },
                { id: "Contact", label: t("contact") },
              ].map((section, index) => (
                <motion.button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="transition-colors relative text-white capitalize font-medium"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100,
                  }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {section.label}
                  <motion.span
                    className="absolute bottom-0 left-0 w-full h-0.5 bg-white origin-left"
                    initial={{ scaleX: 0 }}
                    whileHover={{ scaleX: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                    }}
                  />
                </motion.button>
              ))}

              <div className="flex items-center gap-3 w-auto">
                <AnimatePresence mode="wait">
                  {!Cookies.get("auth_token") && (
                    <motion.button
                      key="signup-btn"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => navigate("/auth")}
                      className="bg-white text-[#7650e3] px-6 py-2.5 text-base rounded-full font-semibold shadow-lg"
                    >
                      {t("get_started_free")}
                    </motion.button>
                  )}

                  {Cookies.get("auth_token") && (
                    <motion.div
                      key="avatar"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2 }}
                      className="cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate("/dashboard")}
                    >
                      <img
                        className="w-11 h-11 rounded-full object-cover border border-white/50 hover:border-white transition-colors"
                        src={
                          user?.avatarUrl
                            ? user?.avatarUrl
                            : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                user?.profile?.fullName || user?.email,
                              )}&background=00000000&color=fff`
                        }
                        alt={user?.profile?.fullName}
                        onError={(e) => {
                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            user?.profile?.fullName || user?.email,
                          )}&background=00000000&color=fff`;
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence mode="wait">
                  {Cookies.get("auth_token") ? (
                    <motion.button
                      key="logout-btn"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => setIsLogoutModalOpen(true)}
                      className="text-white hover:text-[#d7d7fc] transition-colors flex items-center gap-2 font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t("logout")}
                    </motion.button>
                  ) : (
                    <motion.button
                      key="login-btn"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => navigate("/auth")}
                      className="text-white  transition-colors flex items-center gap-2 font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t("login")}
                    </motion.button>
                  )}
                </AnimatePresence>
                <LanguageDropdown alignRight={false} className="text-white" />
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      <section
        id="Home"
        className="  relative min-h-[100vh] flex items-center justify-center overflow-hidden bg-gray-900"
      >
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={OmniVideo}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
        />

        <div className="absolute inset-0 z-0 pointer-events-none w-full h-full will-change-transform">
          <div className="absolute inset-0 bg-gradient-to-br bg-purple-900/70 to-transparent" />
        </div>

        <motion.div
          className="relative z-10 w-fit mx-auto px-4 sm:px-6 lg:px-[10%] text-center rounded-2xl will-change-transform"
          style={{ opacity: heroOpacity, scale: heroScale }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.2,
              type: "spring",
              stiffness: 80,
            }}
            className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight"
          >
            {t("ai_social_media")}
            <br />
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.4,
              type: "spring",
              stiffness: 80,
            }}
            className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto "
          >
            {t("create_schedule_publish")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-wrap items-center justify-center  mb-2 md:mb-8"
          >
            <div className="flex gap-3 mb-5 scale-[1.4] justify-center items-center">
              {[
                {
                  name: "facebook",
                  url: "https://www.facebook.com/share/1H74CpLTCK/",
                  index: 0,
                },
                {
                  name: "instagram",
                  url: "https://www.instagram.com/omnishare.ai/",
                  index: 1,
                },
                
                {
                  name: "linkedin",
                  url: "https://www.linkedin.com/company/omnishare-ai",
                  index: 3,
                },
                {
                  name: "tiktok",
                  url: "https://www.tiktok.com/@omnishare.ai",
                  index: 4,
                },
                {
                  name: "youtube",
                  url: "https://www.youtube.com/@OmniShare-ai",
                  index: 2,
                },
              ].map((icons) => (
                <motion.button
                  key={icons.index}
                  onClick={() => window.open(icons.url, "_blank")}
                  className="will-change-transform"
                  whileHover={{
                    scale: 1.3,
                    rotate: 10,
                    y: -5,
                    transition: { delay: 0 },
                  }}
                  initial={{ opacity: 0, scale: 0, rotate: -180 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{
                    delay: isInitialMount.current ? 0.8 + icons.index * 0.1 : 0,
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                  }}
                >
                  <svg
                    className="md:w-10 md:h-10 w-7 h-7 text-white transition-colors"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    {icons.index === 0 && (
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    )}
                    {icons.index === 1 && (
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    )}
                    {icons.index === 2 && (
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    )}
                    {icons.index === 3 && (
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    )}
                    {icons.index === 4 && (
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                    )}
                  </svg>
                </motion.button>
              ))}
            </div>
          </motion.div>

          <div className="flex-1 flex sm:flex-row flex-col items-center  gap-4 justify-center">
            <motion.button
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.2,
                delay: 0.8,
                ease: "easeOut",
                // type: "spring",
                // stiffness: 100,
              }}
              onClick={() => openModal(VideoModal)}
              className={`bg-transparent text-white border-2 border-white  px-8 py-3 rounded-full text-lg font-semibold shadow-2xl inline-flex items-center space-x-2 min-w-[230px] justify-center ${Cookies.get("auth_token") ? "":"hidden"}`}
              whileHover={{
  scale: 1.03,
  transition: { type: "tween", duration: 0.08, ease: [0.2, 0.8, 0.2, 1] },
}}
whileTap={{
  scale: 0.97,
  transition: { type: "tween", duration: 0.06, ease: "linear" },
}}
            >
              {t("How_to_use")}
            </motion.button>
            <motion.button
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.2,
                delay: 0.8,
                ease: "easeOut",
                // type: "spring",
                // stiffness: 100,
              }}
              onClick={() => {
                if (Cookies.get("auth_token")) {
                  navigate("/content");
                } else {
                  navigate("/auth");
                }
              }}
              className="bg-white text-[#7650e3]  border-2 border-white px-8 py-3 rounded-full text-lg font-semibold shadow-2xl inline-flex items-center space-x-2 min-w-[230px] justify-center"
              whileHover={{
  scale: 1.03,
  transition: { type: "tween", duration: 0.08, ease: [0.2, 0.8, 0.2, 1] },
}}
whileTap={{
  scale: 0.97,
  transition: { type: "tween", duration: 0.06, ease: "linear" },
}}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={Cookies.get("auth_token") ? "create" : "signup"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center space-x-2"
                >
                  <span>
                    {Cookies.get("auth_token")
                      ? t("create_post")
                      : t("get_started_free")}
                  </span>
                </motion.span>
              </AnimatePresence>
            </motion.button>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-14 md:bottom-16 flex justify-center left-0 right-0 transform "
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-8 h-8 text-white" />
        </motion.div>
      </section>
      <motion.div
        id="Features"
        className="scroll-mt-20"
        initial={{ opacity: 1, y: 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-300px" }}
        transition={{ duration: 1.6, type: "spring", stiffness: 80 }}
      >
        <FeaturesPage />
      </motion.div>
      <section
        id="video"
        className="pb-8 md:py-14 bg-white relative overflow-hidden "
      >
        <motion.div
          className="absolute inset-0 opacity-5"
          style={{ y: useTransform(smoothProgress, [0.3, 0.6], [100, -100]) }}
        >
          <div className="absolute top-40 right-20 w-80 h-80 bg-[#7650e3] rounded-full blur-3xl"></div>
        </motion.div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-4 md:mb-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, type: "spring", stiffness: 80 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-5">
              {t("see_action")}
            </h2>
            <p className="text-xl text-gray-500 font-medium">
              {t("see_action_message")}
            </p>
          </motion.div>

          <motion.div
            className="relative rounded-md overflow-hidden shadow-2xl p-8 md:p-12 max-w-4xl mx-auto bg-gray-900 aspect-video"
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, type: "spring", stiffness: 80 }}
            whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
          >
            <video
              src={IntroVideo}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Crect fill='%23111827' width='1920' height='1080'/%3E%3C/svg%3E"
              className="absolute inset-0 w-full h-full object-cover"
              controls
            />
          </motion.div>
        </div>
      </section>
      <TwoColumnSection />
      <TwoColumnSection2 />

      <section className=" bg-gray-50 relative overflow-hidden ">
        <motion.div
          className="absolute inset-0 opacity-5"
          style={{ y: useTransform(smoothProgress, [0, 1], [0, -200]) }}
        >
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#7650e3] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#6366F1] rounded-full blur-3xl"></div>
        </motion.div>
      </section>

      <section className="py-8 md:py-14 bg-gradient-to-br from-[#d7d7fc] to-white relative overflow-hidden md:px-10">
        <div className="absolute inset-0 opacity-5 m">
          <motion.div
            className="absolute top-20 left-20 w-96 h-96 bg-[#7650e3] rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-96 h-96 bg-[#6366F1] rounded-full blur-3xl"
            animate={{ scale: [1, 1.3, 1], x: [0, -50, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 ">
          <motion.div
            className="text-center mb-4 md:mb-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, type: "spring", stiffness: 80 }}
          >
            <h2 className="text-4xl md:text-5xl mb-4 font-bold text-gray-900 md:mb-5">
              {t("loved_creators")}
            </h2>
            <p className="text-xl text-gray-500 mb-4 font-medium md:mb-5">
              {t("loved_creators_message")}
            </p>
          </motion.div>

          <div className="relative pb-8 md:px-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentReviewIndex}
                initial={{
                  opacity: 0,
                  x: slideDirection === "left" ? 100 : -100,
                  scale: 0.9,
                }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{
                  opacity: 0,
                  x: slideDirection === "left" ? -100 : 100,
                  scale: 0.9,
                }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
                className="bg-white rounded-md shadow-2xl p-8 md:p-12 max-w-4xl mx-auto"
              >
                <div className="flex items-center justify-center mb-6">
                  {[...Array(reviews[currentReviewIndex].rating)].map(
                    (_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0, rotate: -180 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{
                          delay: i * 0.1,
                          type: "spring",
                          stiffness: 200,
                        }}
                      >
                        <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                      </motion.div>
                    ),
                  )}
                </div>

                <p className="text-xl md:text-2xl text-black text-center mb-8 leading-relaxed italic">
                  "{reviews[currentReviewIndex].text}"
                </p>

                <div className="flex items-center justify-center space-x-4">
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-[#7650e3] to-[#6366F1] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg"
                    whileHover={{ scale: 1.15, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    {reviews[currentReviewIndex].avatar}
                  </motion.div>
                  <div className="text-left">
                    <p className="font-bold text-gray-900 text-xl">
                      {reviews[currentReviewIndex].name}
                    </p>
                    <p className="text-gray-500 font-medium text-lg">
                      {reviews[currentReviewIndex].role}
                    </p>
                    <p className="text-gray-500 font-medium text-base">
                      {reviews[currentReviewIndex].company}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <motion.button
              onClick={prevReview}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white rounded-full p-3 shadow-xl hover:shadow-2xl transition-all z-20"
              whileHover={{
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.25)",
                scale: 1.1,
              }}
              whileTap={{ scale: 0.95 }}
              aria-label="Previous review"
            >
              <ChevronLeft className="w-6 h-6 text-black" />
            </motion.button>

            <motion.button
              onClick={nextReview}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white rounded-full p-3 shadow-xl hover:shadow-2xl transition-all z-20"
              whileHover={{
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.25)",
                scale: 1.1,
              }}
              whileTap={{ scale: 0.95 }}
              aria-label="Next review"
            >
              <ChevronRight className="w-6 h-6 text-black" />
            </motion.button>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center gap-2 mt-8"
            >
              {reviews.map((_, idx) => (
                <motion.button
                  key={idx}
                  onClick={() => {
                    setSlideDirection(
                      idx > currentReviewIndex ? "left" : "right",
                    );
                    setTimeout(() => {
                      setCurrentReviewIndex(idx);
                      setSlideDirection(null);
                    }, 150);
                  }}
                  className={`h-2.5 rounded-full transition-all ${
                    idx === currentReviewIndex
                      ? "bg-[#7650e3] w-8"
                      : "bg-gray-300 w-2.5 hover:bg-gray-400"
                  }`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label={`Go to review ${idx + 1}`}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section
        id="Faq"
        className=" scroll-mt-20 max-w-4xl md:px-5 pt-8 md:pt-14 lg:mx-auto lg:px-0 "
      >
        <div className="max-w-4xl  md:mx-auto px-4 md:px-0 ">
          <motion.div
            className="text-center mb-4 md:mb-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, type: "spring", stiffness: 80 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-5">
              {t("faq2")}
            </h2>
            <p className="text-xl text-gray-500 font-medium mb-4 md:mb-5 ">
              {t("faq_message")}
            </p>
          </motion.div>

          <motion.div
            className="space-y-4"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            {displayedFaqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-md shadow-md overflow-hidden transition-all hover:shadow-lg"
                whileHover={{ x: 5 }}
              >
                <button
                  onClick={() =>
                    setExpandedFaqIndex(
                      expandedFaqIndex === index ? null : index,
                    )
                  }
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  aria-expanded={expandedFaqIndex === index}
                >
                  <span className="font-Medium text-gray-900 text-xl leading-relaxed pr-8">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: expandedFaqIndex === index ? 180 : 0 }}
                    transition={{
                      duration: 0.3,
                      type: "spring",
                      stiffness: 200,
                    }}
                  >
                    <ChevronDown className="w-6 h-6 text-[#7650e3]" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {expandedFaqIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 text-gray-500 text-lg leading-relaxed ">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-6 text-center">
            <motion.button
              onClick={() => {
                if (visibleCount > 6) {
                  setVisibleCount(6);
                  setExpandedFaqIndex(null); // Critical!
                } else {
                  setVisibleCount(faqs.length);
                }
              }}
              className="bg-white text-[#7650e3] hover:bg-[#d7d7fc] border border-[#7650e3] text-base font-semibold px-8 py-3 rounded-full shadow-lg inline-flex items-center space-x-2"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 30px rgba(118, 80, 227, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              {visibleCount === 6 ? t("load_more") : t("show_less")}
              <motion.div
                animate={{ rotate: visibleCount === faqs.length ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDown className="w-7 h-5 pl-2" />
              </motion.div>
            </motion.button>
          </div>
        </div>
      </section>

      <section
        
        className="scroll-mt-20 py-8  md:px-5 lg:px-0 md:pt-14 md:pb-8 bg-white"
      >
        <div  className="max-w-4xl mx-4 md:mx-auto ">
          <motion.div
            className="text-center md:mb-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, type: "spring", stiffness: 80 }}
          >
            <h2  className="text-4xl md:text-5xl font-bold text-gray-900 md:mb-5">
              <Trans i18nKey="contact_us" components={{ br: <br /> }} />
            </h2>
            <p className="text-xl text-gray-500 font-medium mb-5">
              {t("contact_us_message")}
            </p>
            {/* <motion.button
              onClick={() => setShowContactForm(!showContactForm)}
              className="bg-white text-[#7650e3]  hover:bg-[#d7d7fc] border border-[#7650e3] px-8 py-3 rounded-full shadow-lg inline-flex items-center space-x-2"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 30px rgba(118, 80, 227, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-base font-semibold">
                {showContactForm ? t("hide_form") : t("get_in_touch")}
              </span>
              <motion.div
                animate={{ rotate: showContactForm ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDown className="w-5 h-5" />
              </motion.div>
            </motion.button> */}
          </motion.div>

          <AnimatePresence>
            
              <motion.form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-6 pt-2"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div  className="grid md:grid-cols-2 gap-6">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, type: "spring" }}
                  >
                    <label className="block text-sm font-semibold text-black mb-2">
                      {t("first_name")}
                    </label>
                    <input
                      {...register("firstName")}
                      type="text"
                      className="w-full px-4 py-2.5 border border-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 transition-all"
                      placeholder={t("first_name")}
                    />

                    {/* ❗ Zod Error Here */}
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.firstName.message}
                      </p>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, type: "spring" }}
                  >
                    <label className="block text-sm font-semibold text-black mb-2">
                      {t("last_name")}
                    </label>
                    <input
                      {...register("lastName")}
                      type="text"
                      className="w-full px-4 py-2.5 border border-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 transition-all"
                      placeholder={t("last_name")}
                    />

                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.lastName.message}
                      </p>
                    )}
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, type: "spring" }}
                >
                  <label className="block text-sm font-semibold text-black mb-2">
                    {t("email")}
                  </label>
                  <input
                    {...register("email")}
                    type="text"
                    className="w-full px-4 py-2.5 border border-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 transition-all"
                    placeholder={t("email_address")}
                  />

                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, type: "spring" }}
                >
                  <label className="block text-sm font-semibold text-black mb-2">
                    {t("message")}
                  </label>
                  <textarea
                    {...register("message")}
                    rows={6}
                    className="w-full px-4 py-2.5 border border-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 transition-all"
                    placeholder={t("message_placeholder")}
                  />

                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.message.message}
                    </p>
                  )}
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#7650e3] hover:text-[#7650e3] border border-[#7650e3] hover:bg-[#d7d7fc] text-white py-3 rounded-md font-semibold text-lg shadow-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, type: "spring" }}
                  whileHover={{
                    scale: 1.02,
                    boxShadow: "0 10px 30px rgba(118, 80, 227, 0.3)",
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? t("sending") : t("send_message")}
                </motion.button>
              </motion.form>
           
          </AnimatePresence>
        </div>
      </section>
      
      <CommunitySignup />
      
      <div className="relative bg-theme-secondary ">
        <motion.footer
          className="w-full  py-4 text-center text-sm theme-text-light"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className=" w-full mx-auto md:px-[10%] px-3 flex flex-col justify-between sm:flex-row items-center md:justify-between ">
            <span className="">© {new Date().getFullYear()} OmniShare</span>

            <div className="hidden lg:flex flex-row gap-4 justify-center lg:-mr-36">
              <a
  target="_blank"
  rel="noreferrer"
  className="hover:opacity-80 transition-opacity"
  href="https://www.facebook.com/share/1H74CpLTCK/"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M12 0C5.373 0 0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073c0-6.627-5.373-12-12-12z" fill="white"/>
  </svg>
</a>

{/* Instagram - White Glyph */}
<a
  target="_blank"
  rel="noreferrer"
  className="hover:opacity-80 transition-opacity"
  href="https://www.instagram.com/omnishare.ai/"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="white"/>
  </svg>
</a>

{/* LinkedIn - White Rounded Square with Cut-out 'in' */}
<a
  target="_blank"
  rel="noreferrer"
  className="hover:opacity-80 transition-opacity"
  href="https://www.linkedin.com/company/omnishare-ai"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M0 4a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4zm7.12 16.452H3.558V8.995H7.12v11.457zM5.34 7.433a2.064 2.064 0 1 1 0-4.128 2.064 2.064 0 0 1 0 4.128zM20.452 20.452h-3.558v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667h-3.558V8.995h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.291z" fill="white"/>
  </svg>
</a>

{/* TikTok - White Glyph */}
<a
  target="_blank"
  rel="noreferrer"
  className="hover:opacity-80 transition-opacity"
  href="https://www.tiktok.com/@omnishare.ai"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="24" height="24" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path d="M120 0h40c0 22 18 40 40 40v40c-14 0-27-4-40-12v88c0 44-36 80-80 80S0 200 0 156c0-44 36-80 80-80h40v40H80c-22 0-40 18-40 40s18 40 40 40 40-18 40-40V0z" fill="white"/>
  </svg>
</a>

{/* YouTube - White Play Button with Cut-out Triangle */}
<a
  target="_blank"
  rel="noreferrer"
  href="https://www.youtube.com/@OmniShare-ai"
  className="hover:opacity-80 transition-opacity"
  onClick={(e) => e.stopPropagation()}
>
  <svg width="29" height="29" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[20px] min-h-[20px] shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="white"/>
  </svg>
</a>
              
            </div>

            <div className="flex flex-wrap justify-center space-x-3 text-sm theme-text-light">
              <Link
                to="/privacy"
                onClick={() => window.scrollTo(0, 0)}
                className="transition-colors duration-200 underline"
              >
                {t("privacy_policy")}
              </Link>

              <Link
                to="/terms"
                onClick={() => window.scrollTo(0, 0)}
                className="transition-colors duration-200 underline "
              >
                {t("terms_service")}
              </Link>

              <Link
                to="/support"
                onClick={() => window.scrollTo(0, 0)}
                className="transition-colors duration-200 underline"
              >
                {t("support")}
              </Link>
            </div>
          </div>
        </motion.footer>
      </div>
      <div id="Contact"></div>
      {isLogoutModalOpen && (
  <LogoutModal
    close={() => setIsLogoutModalOpen(false)}
    t={t}
    onConfirmAction={() => {
      setIsLogoutModalOpen(false);
      handleLogout();
    }}
  />
)}
    </div>
    
  );
}

export default HomePage;
